import React, { type JSX, type PropsWithChildren, useId } from 'react'
import styled from '@emotion/styled'
import {
  Placement,
  Strategy,
  arrow,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius } from '@mobi/component-library/Foundations'
import type { TooltipId } from './TooltipId'

type Props = PropsWithChildren<{
  /** Element reference to trigger tooltip from. */
  triggerElRef: React.RefObject<HTMLElement | null>
  /** Tooltip reference key to be stored in local storage. */
  tooltipId: TooltipId
  /** Distance in pixels away from element reference along main axis. */
  offsetYPx?: number
  /** Distance in pixels along the cross axis. */
  offsetXPx?: number
  /** Callback for determining whether the user has seen this tooltip before. */
  hasTooltipBeenSeen: (tooltipId: TooltipId) => boolean
  /** Callback when tooltip is dismissed. Useful for setting flag of whether tooltip has been seen or for iterating sequenced tooltips. */
  onDismissed?: (tooltipId: TooltipId) => void
  /** Title of the tooltip text, formatted differently to the children elements. */
  title?: string
  /**
   * Changes the placement of the tooltip to one that will fit if the initially specified placement does not.
   * (See: https://floating-ui.com/docs/flip)
   */
  flipElement?: boolean
  /** End date of when tooltip should render until. */
  untilDate?: Date
  /** Whether to hide tooltip. */
  isHidden?: boolean
  /** Colour of the tooltip. */
  color?: TooltipColor
  /** Whether to cross icon */
  shouldHideCrossIcon?: boolean
  /** Whether to enable single instance tracking for this tooltip. When enabled, only one instance of this tooltip can be shown at a time. */
  shouldOnlyRenderSingleInstance?: boolean
  /**
   * Whether the position of the tooltip is fixed or absolute
   * (See: https://floating-ui.com/docs/usefloating) under strategy
   */
  strategyPosition?: Strategy
  /** Relative to reference, where should popover be placed */
  preferredPlacement?: Placement
}>

const singleInstanceTrackingMap = new Map<TooltipId, string>()

/**
 * Tooltip component that is displayed if it has not been previously dismissed (seen).
 */
export const Tooltip = ({
  triggerElRef,
  tooltipId,
  offsetYPx = 0,
  offsetXPx = 0,
  hasTooltipBeenSeen,
  title = '',
  children,
  onDismissed,
  flipElement = true,
  untilDate,
  isHidden = false,
  color = 'blue',
  shouldHideCrossIcon = false,
  shouldOnlyRenderSingleInstance = false,
  strategyPosition = 'absolute',
  preferredPlacement = 'bottom',
}: Props): Nullable<JSX.Element> | undefined => {
  const instanceId = useId()
  const arrowRef = React.useRef<HTMLDivElement>(null)
  const [isBeforeUntilDate] = React.useState(() =>
    untilDate ? new Date().getTime() <= untilDate.getTime() : true
  )
  const [isDismissed, setIsDismissed] = React.useState(false)

  React.useEffect(() => {
    if (!shouldOnlyRenderSingleInstance) return
    if (singleInstanceTrackingMap.has(tooltipId)) return
    singleInstanceTrackingMap.set(tooltipId, instanceId)
    return () => {
      singleInstanceTrackingMap.delete(tooltipId)
    }
  }, [tooltipId, instanceId, shouldOnlyRenderSingleInstance])

  React.useEffect(() => {
    setIsDismissed(isHidden)
  }, [isHidden])

  const shouldRender = React.useMemo(
    () => !isDismissed && isBeforeUntilDate && !hasTooltipBeenSeen(tooltipId),
    [isDismissed, isBeforeUntilDate, hasTooltipBeenSeen, tooltipId]
  )

  const middleware = [
    offset({
      mainAxis: offsetYPx ?? 0,
      crossAxis: offsetXPx ?? 0,
    }),
    shift({ padding: 5 }),
    arrow({ element: arrowRef }),
  ]
  if (flipElement) middleware.push(flip())

  const {
    refs,
    placement,
    floatingStyles,
    update: updatePosition,
    middlewareData,
  } = useFloating({
    middleware,
    elements: { reference: triggerElRef.current },
    strategy: strategyPosition,
    placement: preferredPlacement,
  })

  const handleDismiss = React.useCallback(() => {
    setIsDismissed(true)
    onDismissed?.(tooltipId)
  }, [tooltipId, onDismissed])

  // Update Position on Changes to Trigger Element Size
  React.useEffect(() => {
    const targetNode = triggerElRef.current
    if (!shouldRender || !window.ResizeObserver || !targetNode) return undefined
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(targetNode)
    return () => resizeObserver.disconnect()
  }, [triggerElRef, updatePosition, shouldRender])

  // Update Position on DOM Changes (additions/removals)
  React.useEffect(() => {
    const targetNode = triggerElRef.current
    if (!shouldRender || !targetNode) return undefined
    const mutationObserver = new MutationObserver(updatePosition)
    mutationObserver.observe(document.body, { childList: true, subtree: true })
    return () => mutationObserver.disconnect()
  }, [triggerElRef, updatePosition, shouldRender])

  // Handle Dismiss on Reference Node Click
  React.useEffect(() => {
    const targetNode = triggerElRef.current
    if (!shouldRender || !targetNode) return undefined
    targetNode.addEventListener('click', handleDismiss)
    return () => targetNode.removeEventListener('click', handleDismiss)
  }, [shouldRender, handleDismiss, triggerElRef])

  if (!shouldRender) return null

  // Only apply single instance tracking if enabled
  if (shouldOnlyRenderSingleInstance && singleInstanceTrackingMap.has(tooltipId)) {
    const singleInstanceId = singleInstanceTrackingMap.get(tooltipId)
    if (singleInstanceId !== instanceId) return null
  }

  return (
    <TooltipStyled
      color={color}
      data-testid={`tooltip-${tooltipId}`}
      ref={refs.setFloating}
      style={floatingStyles}
      role='log'
      aria-live='polite'
      onClick={handleDismiss}
    >
      <div style={tooltipColors[color]}>
        <span>
          {title ? <strong>{title}</strong> : null}
          {children}
        </span>

        {!shouldHideCrossIcon ? (
          <span className='tooltip__close-icon'>
            <Icon name='SolidXClose' color={colors.white} size='1.5rem' />
          </span>
        ) : null}

        <div
          ref={arrowRef}
          style={{
            left: middlewareData.arrow?.x ? `${middlewareData.arrow.x}px` : '',
            top: middlewareData.arrow?.y ? `${middlewareData.arrow.y}px` : '',
            right: '',
            bottom: '',
            [{
              top: 'bottom',
              right: 'left',
              bottom: 'top',
              left: 'right',
            }[placement.split('-')[0]] as string]: '-4px',
          }}
        />
      </div>
    </TooltipStyled>
  )
}

// ======
// Styles
// ======

const tooltipColors: Record<
  TooltipStyledProps['color'],
  Pick<React.CSSProperties, 'backgroundColor' | 'color'>
> = {
  blue: { backgroundColor: colors.mariner[600], color: colors.white },
  black: { backgroundColor: colors.black, color: colors.white },
  white: { backgroundColor: colors.white, color: colors.neutral[900] },
}

const TooltipStyled = styled.div({
  zIndex: 3,

  '> div': {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    borderRadius: radius.md,
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)',
    maxWidth: '32rem',
    fontFamily: font.family.primary,
    fontWeight: font.weight.regular,
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,

    animation: 'animateIn 0.2s cubic-bezier(.26, .53, .74, 1.48)',

    '> div': {
      position: 'absolute',
      width: '0.8rem',
      height: '0.8rem',
      backgroundColor: 'inherit',
      transform: 'rotate(45deg)',
    },

    ['.tooltip__close-icon']: { opacity: 0.6, padding: '0.75rem' },

    strong: {
      display: 'block',
      textTransform: 'uppercase',
      fontStyle: 'italic',
      paddingRight: '0.5rem',
      fontSize: '1.2rem',
    },

    a: { color: 'inherit' },
  },

  '@keyframes animateIn': {
    from: { opacity: 0, transform: 'scale(0.8, 0.8)' },
    to: { opacity: 1, transform: 'scale(1, 1)' },
  },
})

// =====
// Types
// =====

type TooltipColor = 'blue' | 'black' | 'white'

type TooltipStyledProps = {
  /** The color variant of the tooltip */
  color: TooltipColor
}
